import { useNavigate } from "react-router-dom";
import {
    User,
    Shield,
    ShieldCheck,
    Wallet as WalletIcon,
    Banknote,
    Receipt,
    Sparkles,
    Rocket,
    BarChart3,
    Bell,
    Briefcase,
    MessageCircle,
    HeartPulse,
    Moon,
    Sun,
    LifeBuoy,
    AlertOctagon,
    Info,
    LogOut,
    ChevronRight,
    BadgeCheck,
} from "lucide-react";
import { PageHeader, Row, SectionTitle } from "@/screens/_shared";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import InitialAvatar from "@/components/InitialAvatar";

const Chevron = () => <ChevronRight size={16} className="text-muted-foreground" />;

const Settings = () => {
    const navigate = useNavigate();
    const { user, signOut } = useAuth();
    const { theme, setTheme, toggleTheme, usingSystem, resetToSystem } = useTheme();

    const handleLogout = () => {
        signOut();
        navigate("/auth", { replace: true });
    };

    return (
        <div className="screen" data-testid="settings-screen">
            <PageHeader title="Settings" />
            <div className="flex-1 overflow-y-auto no-scrollbar pb-10">
                {/* Account summary */}
                <div className="px-5 pt-4">
                    <div className="flex items-center gap-3 p-3.5 rounded-2xl border border-border bg-card shadow-soft">
                        {user?.avatar ? (
                            <img src={user.avatar} alt="" className="h-12 w-12 rounded-full object-cover" />
                        ) : (
                            <InitialAvatar name={user?.fullName || "?"} size={48} />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-[14px] font-semibold truncate flex items-center gap-1.5">
                                {user?.fullName || "—"}
                                {user?.verified && <BadgeCheck size={14} className="text-primary" />}
                            </div>
                            <div className="text-[12px] text-muted-foreground truncate">
                                @{user?.username}
                            </div>
                        </div>
                        <span
                            className={[
                                "px-2.5 py-1 rounded-full text-[10.5px] font-semibold",
                                user?.premium
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-secondary text-muted-foreground",
                            ].join(" ")}
                        >
                            {user?.premium ? "PREMIUM" : "FREE"}
                        </span>
                    </div>
                </div>

                <SectionTitle>Account</SectionTitle>
                <div className="px-2">
                    <Row icon={User}        label="Account & Profile" right={<Chevron />} onClick={() => navigate("/profile/edit")} testId="settings-row-profile" />
                    {user?.accountType !== "business" && (
                        <Row
                            icon={Briefcase}
                            label="Upgrade to Business"
                            right={<Chevron />}
                            onClick={() => navigate("/profile/upgrade-business")}
                            testId="settings-row-upgrade-business"
                        />
                    )}
                    <Row icon={Shield}      label="Privacy & Security" right={<Chevron />} onClick={() => navigate("/safety")} testId="settings-row-privacy" />
                    <Row icon={ShieldCheck} label="Verification"       right={user?.verified ? <BadgeCheck size={16} className="text-primary" /> : <Chevron />} onClick={() => navigate(user?.accountType === "business" ? "/verification/business" : "/verification/personal")} testId="settings-row-verification" />
                </div>

                <SectionTitle>Finance</SectionTitle>
                <div className="px-2">
                    <Row icon={WalletIcon} label="Wallet"          right={<Chevron />} onClick={() => navigate("/wallet")} testId="settings-row-wallet" />
                    <Row icon={Banknote}   label="Payout Methods"  right={<Chevron />} onClick={() => navigate("/wallet/payouts")} testId="settings-row-payouts" />
                    <Row icon={Receipt}    label="Transactions"    right={<Chevron />} onClick={() => navigate("/wallet/transactions")} testId="settings-row-transactions" />
                </div>

                <SectionTitle>Grow</SectionTitle>
                <div className="px-2">
                    <Row icon={Sparkles}  label="Premium"           right={<Chevron />} onClick={() => navigate("/premium")}  testId="settings-row-premium" />
                    <Row icon={Rocket}    label="Boost / Promote"   right={<Chevron />} onClick={() => navigate("/boost")}    testId="settings-row-boost" />
                    <Row icon={BarChart3} label="Insights"          right={<Chevron />} onClick={() => navigate("/insights")} testId="settings-row-insights" />
                </div>

                <SectionTitle>Preferences</SectionTitle>
                <div className="px-2">
                    <Row
                        icon={MessageCircle}
                        label="Messaging & privacy"
                        right={<Chevron />}
                        onClick={() => navigate("/settings/messaging")}
                        testId="settings-row-messaging"
                    />
                    <Row
                        icon={Bell}
                        label="Notifications"
                        right={<Chevron />}
                        onClick={() => navigate("/notifications")}
                        testId="settings-row-notifications"
                    />
                    <Row
                        icon={HeartPulse}
                        label="Safety Center"
                        right={<Chevron />}
                        onClick={() => navigate("/safety")}
                        testId="settings-row-safety"
                    />
                    <div className="w-full flex items-center gap-3 p-3.5 rounded-2xl" data-testid="settings-row-appearance">
                        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                            {theme === "dark" ? <Moon size={18} /> : <Sun size={18} />}
                        </span>
                        <span className="flex-1 text-[14px] font-medium">Appearance</span>
                        <div className="inline-flex rounded-full bg-secondary p-0.5" data-testid="settings-theme-group">
                            <ThemeChip label="Light"  active={!usingSystem && theme === "light"} onClick={() => setTheme("light")} testId="settings-theme-light" />
                            <ThemeChip label="Dark"   active={!usingSystem && theme === "dark"}  onClick={() => setTheme("dark")} testId="settings-theme-dark" />
                            <ThemeChip label="System" active={usingSystem}                        onClick={resetToSystem}         testId="settings-theme-system" />
                        </div>
                    </div>
                </div>

                <SectionTitle>Support</SectionTitle>
                <div className="px-2">
                    <Row icon={LifeBuoy}    label="Contact Support"   right={<Chevron />} onClick={() => navigate("/support")} testId="settings-row-support" />
                    <Row icon={LifeBuoy}    label="Help Center"       right={<Chevron />} onClick={() => navigate("/help")}    testId="settings-row-help" />
                    <Row icon={AlertOctagon} label="Report a Problem" right={<Chevron />} onClick={() => navigate("/report")}  testId="settings-row-report" />
                    <Row icon={Info}        label="About Neyra"       right={<Chevron />} onClick={() => navigate("/about")}   testId="settings-row-about" />
                </div>

                <div className="px-2 mt-4">
                    <Row icon={LogOut} label="Log out" danger onClick={handleLogout} testId="settings-row-logout" />
                </div>
            </div>
        </div>
    );
};

const ThemeChip = ({ label, active, onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className={[
            "h-8 px-3 rounded-full text-[11.5px] font-semibold transition-colors",
            active ? "bg-primary text-primary-foreground" : "text-muted-foreground",
        ].join(" ")}
    >
        {label}
    </button>
);

export default Settings;
